import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';
import SettingsNav from './SettingsNav';
import EnterpriseModal from './EnterpriseModal';
   import EmployerPlansTab, { TAX_NOTE, REFUND_NOTE } from './EmployerPlansTab';
import BillingHistory from './BillingHistory';

const PLAN_META = {
  starter: { label: 'Starter', blurb: 'For small teams getting started' },
  growth: { label: 'Growth', blurb: 'For growing hiring teams' },
  pro: { label: 'Pro', blurb: 'For high-volume hiring' },
  enterprise: { label: 'Enterprise', blurb: 'For large-scale hiring' },
};

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function loadCashfree() {
  return new Promise((resolve) => {
    if (window.Cashfree) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const FONT = {};

const TABS = [
  { key: 'plans', label: '💳 Hiring Plans' },
  // { key: 'hiring', label: '📦 Hiring Packs' },
  // { key: 'payperjob', label: '📝 Pay-Per-Job' },
  { key: 'credits', label: '👤 Candidate Credits' },
  { key: 'boost', label: '⭐ Boost / Featured' },
];

function ComingSoonButton({ label = 'Buy' }) {
  return (
    <button
      type="button"
      disabled
      style={FONT}
      title="Online purchase for this is coming soon — contact support to activate."
      className="w-full py-3 rounded-xl text-sm font-bold border-2 border-[#F3E8FF] text-gray-400 cursor-not-allowed"
    >
      {label} — Coming soon
    </button>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center text-sm text-gray-400 py-10" style={FONT}>
      {text}
    </div>
  );
}

const PRICING_FAQS = [
  { q: 'Is candidate registration free?', a: 'Yes. Job seekers can create their profiles and explore opportunities without a candidate registration fee.' },
  { q: 'Can I post my first job for free?', a: 'Eligible verified employers can access the Launch package, subject to its terms.' },
  { q: 'Can I post multiple jobs?', a: 'Yes. Scale and Business Plus offer multiple job credits, subject to the package rules.' },
  { q: 'Can I renew my package?', a: 'Employers can purchase another package through the available payment process.' },
  { q: 'How are payments activated?', a: 'Successful eligible payments should automatically activate the purchased package and job credits.' },
  { q: 'Are taxes included?', a: 'Applicable taxes are displayed clearly during checkout and on invoices.' },
];

function PricingHero({ onTalkToSales }) {
  return (
    <div className="text-center max-w-2xl mx-auto mb-8">
      <h1 className="inline-block text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">
        Better Hiring. Smarter Spending.
      </h1>
      <p className="text-gray-500 mt-3 leading-relaxed">
        Whether you need one employee or an entire team, PlanB Jobs has a recruitment package designed around your hiring needs.
      </p>
      
    </div>
  );
}

function PricingFaq() {
  return (
    <section className="mt-10 mb-6">
      <h2 className="inline-block text-2xl font-bold tracking-tight mb-4 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">
        Pricing FAQ
      </h2>
      <div className="space-y-3">
        {PRICING_FAQS.map((f) => (
          <details key={f.q} className="group bg-white border border-[#F3E8FF] rounded-2xl px-5 py-4">
            <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-[#3B0764]">
              {f.q}
              <span className="text-[#DB2777] text-2xl leading-none group-open:hidden">+</span>
              <span className="text-[#DB2777] text-2xl leading-none hidden group-open:inline">–</span>
            </summary>
            <p className="text-sm text-gray-500 leading-relaxed mt-3">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function EmployerBilling() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    return ['plans','hiring','payperjob','credits','boost'].includes(t) ? t : 'plans';
  });

  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activating, setActivating] = useState(null);
  const [buyingGrant, setBuyingGrant] = useState(null);
  const [grantConfirm, setGrantConfirm] = useState(null);
  const [records, setRecords] = useState([]);
  const [featuredOptions, setFeaturedOptions] = useState([]);
  const [unused, setUnused] = useState({ job_boost: 0, featured_job: 0 });
  const [myJobs, setMyJobs] = useState([]);
  const [applyJobId, setApplyJobId] = useState('');
  const [applying, setApplying] = useState(false);
 
  const [message, setMessage] = useState('');
  const [autoPayLoading, setAutoPayLoading] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [autoPayEnabled, setAutoPayEnabled] = useState(true);

  const [hiringPacks, setHiringPacks] = useState([]);
  const [jobProducts, setJobProducts] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [jobBoosts, setJobBoosts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  const load = async () => {
    try {
      const [curRes, histRes, plansRes, hiringRes, jobProductsRes, creditPacksRes, jobBoostsRes] = await Promise.all([
        api.get('/company/subscription'),
        api.get('/company/subscription/history'),
        api.get('/plans', { params: { type: 'subscription' } }),
        api.get('/plans', { params: { type: 'hiring' } }),
        api.get('/job-products'),
        api.get('/candidate-credit-packs'),
        api.get('/job-boosts'),
      ]);
      setCurrent(curRes.data);
      setHistory(histRes.data);
      api.get('/company/billing/history').then((r) => setRecords(r.data)).catch(() => {});
      api.get('/featured-job-options')
        .then((r) => {
          console.log('featured options:', r.data);
          setFeaturedOptions(Array.isArray(r.data) ? r.data : []);
        })
        .catch((e) => console.error('featured-job-options failed:', e.response?.status, e.response?.data || e.message));
      api.get('/company/grants/available').then((r) => setUnused(r.data)).catch(() => {});
      api.get('/jobs').then((r) => setMyJobs((r.data || []).filter((j) => j.status === 'active'))).catch(() => {});
      setPlans(plansRes.data);
      setHiringPacks(hiringRes.data);
      setJobProducts(jobProductsRes.data);
      setCreditPacks(creditPacksRes.data);
      setJobBoosts(jobBoostsRes.data);
    } catch (err) {
      console.error('Failed to fetch billing data', err);
    } finally {
      setLoading(false);
    }
  };

  // Handles the case where Cashfree just redirected the user BACK to this
  // page after they paid on Cashfree's hosted checkout page. Cashfree does
  // not give us a JS callback like Razorpay does — instead we recognize the
  // return via query params we set as the returnUrl, then ask our backend
  // to confirm the payment actually went through before activating anything.
  const handleCashfreeReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const cfOrderId = params.get('cf_order_id');
    const cfPlan = params.get('cf_plan');
    const cfCycle = params.get('cf_cycle') || 'monthly';
    const cfGrantType = params.get('cf_grant_type');
    const cfGrantSource = params.get('cf_grant_source');
    const cfGrantLabel = params.get('cf_grant_label') || cfGrantSource;
    const returnTab = params.get('tab') || 'plans';

    if (!cfOrderId || (!cfPlan && !cfGrantType)) return;

    setMessage('Confirming your payment…');
    try {
      if (cfGrantType) {
        await api.post('/company/grants/purchase/verify', {
          gateway: 'cashfree',
          order_id: cfOrderId,
          source_type: cfGrantType,
          source_name: cfGrantSource,
        });
        setMessage(`${cfGrantLabel} purchased successfully.`);
      } else {
        await api.post('/company/subscription/purchase/verify', {
          gateway: 'cashfree',
          order_id: cfOrderId,
          plan_name: cfPlan,
          billing_cycle: cfCycle,
        });
        setMessage(`${cfPlan.charAt(0).toUpperCase() + cfPlan.slice(1)} plan activated.`);
      }
      load();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Payment verification failed. If money was deducted, contact support.');
    } finally {
      // Clean the query params out of the URL so a page refresh doesn't
      // re-trigger verification.
      const cleanUrl = `${window.location.origin}${window.location.pathname}?tab=${returnTab}`;
      window.history.replaceState({}, '', cleanUrl);
    }
  };

  // Customer comes back from Cashfree's mandate page. The webhook activates
  // the plan, which can take a few seconds, so re-check a few times.
  const handleAutoPayReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('autopay') !== 'return') return;

    setMessage('Confirming your auto-pay setup…');
    window.history.replaceState({}, '', `${window.location.origin}${window.location.pathname}?tab=plans`);

    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        await api.post('/company/subscription/autopay/verify').catch(() => {});
        const res = await api.get('/company/subscription');
        if (res.data?.auto_renew) {
          setMessage('Auto-pay activated. Your plan will renew every month.');
          load();
          return;
        }
      } catch (e) { /* keep trying */ }
    }
    setMessage('Your bank is still approving the auto-pay mandate. This can take a few minutes, sometimes longer. Your plan activates once it is approved. If it is still inactive after a day, contact support.');
    load();
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    load().then(() => {
      handleCashfreeReturn();
      handleAutoPayReturn();
      // Bank approval can take longer than the return-page polling, so re-check on every visit.
      api.post('/company/subscription/autopay/verify')
        .then((r) => { if (r.data?.activated) load(); })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  const prevTitle = document.title;
  document.title = 'Affordable Job Posting Packages in India | PlanB Jobs';
  let meta = document.querySelector('meta[name="description"]');
  const created = !meta;
  if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
  const prevDesc = meta.getAttribute('content');
  meta.setAttribute('content', 'Explore affordable job posting plans on PlanB Jobs. Choose Launch, Solo, Scale, Business Plus or Corporate recruitment packages.');
  return () => {
    document.title = prevTitle;
    if (created) meta.remove();
    else if (prevDesc !== null) meta.setAttribute('content', prevDesc);
  };
}, []);

  const handleAutoPay = async (planName) => {
    setAutoPayLoading(planName);
    setMessage('');
    try {
      const { data } = await api.post('/company/subscription/autopay/order', { plan_name: planName });

      const ok = await loadCashfree();
      if (!ok) throw new Error('Could not load the payment window. Check your connection.');

      const cashfree = window.Cashfree({ mode: data.env === 'sandbox' ? 'sandbox' : 'production' });
      // Redirects the whole page to Cashfree. The user returns via the
      // return_url set on the server, handled by handleAutoPayReturn().
      await cashfree.subscriptionsCheckout({
        subsSessionId: data.subscription_session_id,
        redirectTarget: '_self',
      });
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Could not start auto-pay.');
      setAutoPayLoading(null);
    }
  };

  const handleCancelAutoPay = async () => {
    if (!window.confirm('Cancel auto-renew? Your plan stays active until its end date.')) return;
    setCancelling(true);
    setMessage('');
    try {
      const res = await api.post('/company/subscription/autopay/cancel');
      setMessage(res.data?.message || 'Auto-pay cancelled.');
      load();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not cancel auto-pay.');
    } finally {
      setCancelling(false);
    }
  };

  const handleScheduleDowngrade = async (planName) => {
    setMessage('');
    try {
      const res = await api.post('/company/subscription/schedule-downgrade', {
        plan_name: planName,
        billing_cycle: billingCycle,
      });
      setMessage(res.data?.message || 'Downgrade scheduled.');
      load();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not schedule downgrade.');
    }
  };

  const handleApplyCredit = async (kind) => {
    if (!applyJobId) { setMessage('Please select a job first.'); return; }
    setApplying(true);
    setMessage('');
    try {
      const res = await api.post('/company/grants/apply-boost', {
        job_posting_id: Number(applyJobId),
        source_type: kind,
      });
      setMessage(res.data?.message || 'Applied to your job.');
      setApplyJobId('');
      load();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not apply.');
    } finally {
      setApplying(false);
    }
  };

  const handleActivate = async (planName) => {
    setActivating(planName);
    setMessage('');
    try {
      const { data: order } = await api.post('/company/subscription/purchase/order', {
        plan_name: planName,
        billing_cycle: billingCycle,
      });

      if (order.gateway === 'cashfree') {
        const ok = await loadCashfree();
        if (!ok) throw new Error('Could not load the payment window. Check your connection.');

        const cashfree = window.Cashfree({ mode: order.env === 'sandbox' ? 'sandbox' : 'production' });
        const returnUrl = `${window.location.origin}${window.location.pathname}` +
          `?tab=plans&cf_order_id=${encodeURIComponent(order.order_id)}` +
          `&cf_plan=${encodeURIComponent(planName)}&cf_cycle=${encodeURIComponent(billingCycle)}`;

        // This REDIRECTS the whole page to Cashfree's hosted checkout —
        // it does not return control to this function. The user comes back
        // via returnUrl above, handled by handleCashfreeReturn() on load.
        await cashfree.checkout({
          paymentSessionId: order.payment_session_id,
          returnUrl,
        });
        return;
      }

      // Razorpay path — unchanged popup behavior.
      const ok = await loadRazorpay();
      if (!ok) throw new Error('Could not load the payment window. Check your connection.');

      const rzp = new window.Razorpay({
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'PlanB Jobs',
        description: `${planName.charAt(0).toUpperCase() + planName.slice(1)} plan (${billingCycle})`,
        handler: async (resp) => {
          try {
            await api.post('/company/subscription/purchase/verify', {
              gateway: 'razorpay',
              order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan_name: planName,
              billing_cycle: billingCycle,
            });
            setMessage(`${planName.charAt(0).toUpperCase() + planName.slice(1)} plan activated.`);
            load();
          } catch (err) {
            setMessage(err.response?.data?.message || 'Payment verification failed. If money was deducted, contact support.');
          } finally {
            setActivating(null);
          }
        },
        modal: { ondismiss: () => setActivating(null) },
      });
      rzp.open();
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Could not start payment.');
      setActivating(null);
    }
  };

  const handleBuyGrant = async (sourceType, sourceName) => {
    const key = `${sourceType}:${sourceName}`;
    setBuyingGrant(key);
    setMessage('');
    try {
      const { data: order } = await api.post('/company/grants/purchase/order', {
        source_type: sourceType,
        source_name: sourceName,
      });

      if (order.gateway === 'cashfree') {
        const ok = await loadCashfree();
        if (!ok) throw new Error('Could not load the payment window. Check your connection.');

        const cashfree = window.Cashfree({ mode: order.env === 'sandbox' ? 'sandbox' : 'production' });
        const currentTab = new URLSearchParams(window.location.search).get('tab') || 'plans';
        const returnUrl = `${window.location.origin}${window.location.pathname}` +
          `?tab=${encodeURIComponent(currentTab)}&cf_order_id=${encodeURIComponent(order.order_id)}` +
          `&cf_grant_type=${encodeURIComponent(sourceType)}&cf_grant_source=${encodeURIComponent(sourceName)}` +
          `&cf_grant_label=${encodeURIComponent(order.label || sourceName)}`;

        // Redirects the whole page — control returns via handleCashfreeReturn on load.
        await cashfree.checkout({
          paymentSessionId: order.payment_session_id,
          returnUrl,
        });
        return;
      }

      // Razorpay path — unchanged popup behavior.
      const ok = await loadRazorpay();
      if (!ok) throw new Error('Could not load the payment window. Check your connection.');

      const rzp = new window.Razorpay({
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'PlanB Jobs',
        description: order.label || sourceName,
        handler: async (resp) => {
          try {
            await api.post('/company/grants/purchase/verify', {
              gateway: 'razorpay',
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              source_type: sourceType,
              source_name: sourceName,
            });
            setMessage(`${sourceName} purchased successfully.`);
            load();
          } catch (err) {
            setMessage(err.response?.data?.message || 'Payment verification failed. If money was deducted, contact support.');
          } finally {
            setBuyingGrant(null);
          }
        },
        modal: { ondismiss: () => setBuyingGrant(null) },
      });
      rzp.open();
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Could not start payment.');
      setBuyingGrant(null);
    }
  };

  // Autopay is monthly only, and the toggle is hidden once auto-pay is already on.
  const showAutoPayToggle = billingCycle === 'monthly' && !current?.auto_renew;
  const useAutoPay = autoPayEnabled && showAutoPayToggle;

  const currentPlanBadge = (
    <div className="rounded-2xl p-5 sm:p-6 mb-6 bg-gradient-to-r from-[#6D28D9] via-[#9333EA] to-[#EC4899] shadow-lg">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-white/90 text-lg">🛡️</span>
        <p className="text-white font-bold" style={FONT}>
          Current plan:{' '}
          <span className="font-extrabold">
            {current?.plan_name && current.plan_name !== 'free' && current.plan_name !== 'Free'
              ? current.plan_name.charAt(0).toUpperCase() + current.plan_name.slice(1)
              : '—'}
          </span>
          {current?.expires_at && (
            <span className="font-normal text-white/85">
              {' '}· {current?.auto_renew ? 'Renews' : 'Expires'} {new Date(current.expires_at).toLocaleDateString()}
            </span>
          )}
        </p>
        {current?.pending_plan_name && (
          <span className="text-xs font-bold bg-white/20 text-white px-3 py-1 rounded-full">
            Changes to {current.pending_plan_name} on {current?.expires_at ? new Date(current.expires_at).toLocaleDateString() : 'renewal'}
          </span>
        )}
        {current?.auto_renew && (
          <>
            <span className="text-xs font-bold bg-white/20 text-white px-3 py-1 rounded-full">Auto-pay on</span>
            <button
              type="button"
              onClick={handleCancelAutoPay}
              disabled={cancelling}
              style={FONT}
              className="ml-auto text-xs font-bold bg-white text-[#9333EA] px-4 py-1.5 rounded-full hover:shadow-md disabled:opacity-60"
            >
              {cancelling ? 'Cancelling…' : 'Cancel auto-renew'}
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
   <div className="min-h-screen bg-gradient-to-b from-[#FDF4FF] to-white" style={FONT}>
  <Navbar user={user} />

      {grantConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setGrantConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-extrabold text-[#3B0764] text-lg mb-4">Order summary</h3>
            <div className="divide-y divide-[#F3E8FF] border border-[#F3E8FF] rounded-xl text-sm mb-4">
              <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Item</span><span className="font-semibold text-[#3B0764]">{grantConfirm.item}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Quantity</span><span className="font-semibold text-[#3B0764]">1</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Validity</span><span className="font-semibold text-[#3B0764]">{grantConfirm.validity}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Total</span><span className="font-extrabold text-[#3B0764]">₹{Number(grantConfirm.price).toLocaleString('en-IN')}</span></div>
            </div>
            <p className="text-xs text-gray-500 mb-1">{TAX_NOTE}</p>
            <p className="text-xs text-gray-500 mb-5">{REFUND_NOTE}</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setGrantConfirm(null)} className="flex-1 py-3 rounded-xl text-sm font-bold border-2 border-[#F3E8FF] text-gray-500">Cancel</button>
              <button
                type="button"
                onClick={() => { const g = grantConfirm; setGrantConfirm(null); handleBuyGrant(g.type, g.name); }}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#9333EA] to-[#EC4899]"
              >
                Pay ₹{Number(grantConfirm.price).toLocaleString('en-IN')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        <EmployerSidebar user={user} />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#3B0764]"
              style={FONT}
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} mobile />}

            <h2 className="text-xl sm:text-2xl font-bold text-[#7345FB] mb-1" style={FONT}>Company Settings</h2>
            <p className="text-gray-500 text-sm mb-4" style={FONT}>Manage your team and preferences</p>

            <SettingsNav />

            {loading ? (
              <p className="text-gray-500 text-sm" style={FONT}>Loading...</p>
            ) : (
              <>
                {currentPlanBadge}

                {activeTab === 'plans' && <PricingHero onTalkToSales={() => setShowEnterprise(true)} />}

                <div className="flex flex-wrap gap-2 mb-6 bg-white rounded-xl p-1.5 shadow border border-[#F3E8FF]">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      style={FONT}
                      className={`flex-1 min-w-[140px] px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                        activeTab === tab.key
                          ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white shadow-sm'
                          : 'text-gray-500 hover:bg-[#FDF4FF]'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {message && (
                  <div className="bg-[#FDF4FF] text-[#9333EA] p-3 rounded-lg mb-5 text-sm border border-[#F3E8FF]" style={FONT}>{message}</div>
                )}



                <EnterpriseModal open={showEnterprise} onClose={() => setShowEnterprise(false)} />
                  
                  {activeTab === 'plans' && (
     <EmployerPlansTab
       plans={plans}
       current={current}
       billingCycle={billingCycle}
       onBillingCycleChange={setBillingCycle}
       activating={activating}
       autoPayLoading={autoPayLoading}
       onBuy={(name) => (useAutoPay ? handleAutoPay(name) : handleActivate(name))}
       onAutoPay={handleAutoPay}
       onScheduleDowngrade={handleScheduleDowngrade}
       pendingPlanName={current?.pending_plan_name}
       onFreePlan={() => navigate('/employer/jobs')}
       autoPayOn={autoPayEnabled}
       onAutoPayToggle={() => setAutoPayEnabled((v) => !v)}
     />
   )}

                {activeTab === 'hiring' && (
                  <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 border border-[#F3E8FF]">
                    <h3 className="font-extrabold text-[#3B0764] text-lg mb-1" style={FONT}>📦 Hiring Packs</h3>
                    <p className="text-sm text-gray-500 mb-5" style={FONT}>One-time prepaid packs — no monthly commitment.</p>

                    {hiringPacks.length === 0 ? (
                      <EmptyState text="No hiring packs available right now." />
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {hiringPacks.map((pack) => (
                          <div key={pack.id} className="rounded-2xl p-6 flex flex-col border-2 border-[#F3E8FF] bg-white hover:border-[#EC4899] hover:shadow-xl transition">
                            <p className="text-xs font-bold uppercase tracking-wide text-[#A855F7] mb-2" style={FONT}>{pack.name}</p>
                            <p className="text-3xl font-extrabold mb-0.5 bg-gradient-to-r from-[#9333EA] to-[#EC4899] bg-clip-text text-transparent" style={FONT}>
                              ₹{Number(pack.price_monthly).toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-400 mb-4" style={FONT}>one-time</p>
                            <div className="space-y-2 mb-6 flex-1 text-sm text-gray-600" style={FONT}>
                              <div className="flex items-center gap-2"><span>💼</span><span>{pack.job_post_limit ?? '—'} job posts</span></div>
                              {pack.candidate_credits != null && (
                                <div className="flex items-center gap-2"><span>👤</span><span>{pack.candidate_credits} candidate credits</span></div>
                              )}
                              {pack.featured_jobs ? (
                                <div className="flex items-center gap-2"><span>⭐</span><span>{pack.featured_jobs} featured job{pack.featured_jobs === 1 ? '' : 's'}</span></div>
                              ) : null}
                              {pack.contact_unlocks_per_job != null && (
                                <div className="flex items-center gap-2"><span>📞</span><span>{pack.contact_unlocks_per_job} contact unlocks/job</span></div>
                              )}
                              {pack.per_job_duration_days != null && (
                                <div className="flex items-center gap-2"><span>📅</span><span>Job stays live {pack.per_job_duration_days} days</span></div>
                              )}
                              {pack.pack_validity_days != null && (
                                <div className="flex items-center gap-2"><span>⏳</span><span>Pack valid {pack.pack_validity_days} days</span></div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleBuyGrant('hiring_pack', pack.name)}
                              disabled={buyingGrant === `hiring_pack:${pack.name}`}
                              style={FONT}
                              className="py-3 rounded-xl text-sm font-bold border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#FDF4FF] disabled:opacity-50 transition"
                            >
                              {buyingGrant === `hiring_pack:${pack.name}` ? 'Opening…' : 'Buy pack'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'payperjob' && (
                  <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 border border-[#F3E8FF]">
                    <h3 className="font-extrabold text-[#3B0764] text-lg mb-1" style={FONT}>📝 Pay-Per-Job</h3>
                    <p className="text-sm text-gray-500 mb-5" style={FONT}>Don't want a subscription? Buy individual job posts.</p>

                    {jobProducts.length === 0 ? (
                      <EmptyState text="No pay-per-job options available right now." />
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {jobProducts.map((opt) => (
                          <div key={opt.id} className="rounded-2xl p-6 flex flex-col border-2 border-[#F3E8FF] bg-white hover:border-[#EC4899] hover:shadow-xl transition">
                            <p className="text-xs font-bold uppercase tracking-wide text-[#A855F7] mb-2" style={FONT}>{opt.name}</p>
                            <p className="text-3xl font-extrabold mb-0.5 bg-gradient-to-r from-[#9333EA] to-[#EC4899] bg-clip-text text-transparent" style={FONT}>
                              ₹{Number(opt.price).toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-400 mb-4" style={FONT}>one-time</p>
                            <div className="space-y-2 mb-6 flex-1 text-sm text-gray-600" style={FONT}>
                              <div className="flex items-center gap-2"><span>💼</span><span>{opt.job_posts} job post{opt.job_posts === 1 ? '' : 's'}</span></div>
                              <div className="flex items-center gap-2"><span>📅</span><span>Valid {opt.validity_days} days</span></div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleBuyGrant('job_product', opt.name)}
                              disabled={buyingGrant === `job_product:${opt.name}`}
                              style={FONT}
                              className="py-3 rounded-xl text-sm font-bold border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#FDF4FF] disabled:opacity-50 transition"
                            >
                              {buyingGrant === `job_product:${opt.name}` ? 'Opening…' : 'Buy'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

{activeTab === 'credits' && (() => {
  const unlocksOf = (p) => Number(p.unlocks ?? p.credits) || 1;
  const perUnlock = (p) => Number(p.price) / unlocksOf(p);
  const bestId = creditPacks.length > 1
    ? creditPacks.reduce((a, b) => (perUnlock(b) < perUnlock(a) ? b : a)).id
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 border border-[#F3E8FF]">
      <div className="flex items-center gap-4 mb-6">
        <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#9333EA] to-[#EC4899] text-white text-2xl flex items-center justify-center shadow-lg">👤</span>
        <div>
          <h3 className="font-extrabold text-[#3B0764] text-xl">Candidate Credit Top-Ups</h3>
          <p className="text-sm text-gray-500">Extra candidate-contact unlocks when you run out. Top-ups never expire.</p>
        </div>
      </div>

      {creditPacks.length === 0 ? (
        <EmptyState text="No credit top-up packs available right now." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {creditPacks.map((pack) => {
            const isBest = pack.id === bestId;
            return (
              <div
                key={pack.id}
                className={`relative rounded-2xl overflow-hidden border-2 bg-white transition duration-200 hover:-translate-y-1 hover:shadow-2xl ${
                  isBest ? 'border-[#EC4899] shadow-xl' : 'border-[#F3E8FF] shadow-md'
                }`}
              >
                {isBest && (
                  <span className="absolute top-3 right-3 z-10 text-[10px] font-bold bg-white text-[#EC4899] px-3 py-1 rounded-full shadow">
                    Best Value
                  </span>
                )}

                <div className="relative bg-gradient-to-br from-[#6D28D9] via-[#9333EA] to-[#EC4899] text-white px-6 pt-6 pb-8">
                  <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10"></div>
                  <div className="absolute -left-8 -bottom-10 w-28 h-28 rounded-full bg-white/10"></div>
                  <p className="relative text-xs font-bold uppercase tracking-widest text-white/80">Credit pack</p>
                  <p className="relative text-5xl font-extrabold leading-none mt-2">{pack.credits ?? pack.unlocks}</p>
                  <p className="relative text-sm text-white/90 mt-1">Credits</p>
                </div>

                <div className="p-6">
                  <div className="flex items-end justify-between mb-4">
                    <p className="text-3xl font-extrabold text-[#3B0764]">₹{Number(pack.price).toLocaleString('en-IN')}</p>
                    <p className="text-xs font-semibold text-[#9333EA] bg-[#FDF4FF] px-2.5 py-1 rounded-full">
                      ₹{perUnlock(pack).toFixed(1)} / unlock
                    </p>
                  </div>

                  <ul className="space-y-2 mb-6 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>
                      {pack.unlocks ?? pack.credits} candidate contact unlocks
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>
                      Never expires
                    </li>
                  </ul>

                  <button
                    type="button"
                    onClick={() => setGrantConfirm({ type: 'credit_topup', name: String(pack.id), item: `${pack.credits ?? pack.unlocks} candidate credits`, price: pack.price, validity: 'Never expires' })}
                    disabled={buyingGrant === `credit_topup:${pack.id}`}
                    className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#9333EA] to-[#EC4899] hover:shadow-lg disabled:opacity-50 transition"
                  >
                    {buyingGrant === `credit_topup:${pack.id}` ? 'Opening…' : 'Buy top-up'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
})()}

     {activeTab === 'boost' && (() => {
  const bestBoostId = jobBoosts.length > 1
    ? jobBoosts.reduce((a, b) =>
        (Number(b.price) / Number(b.duration_days) < Number(a.price) / Number(a.duration_days) ? b : a)
      ).id
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 border border-[#F3E8FF]">
      <div className="flex items-center gap-4 mb-6">
        <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#9333EA] to-[#EC4899] text-white text-2xl flex items-center justify-center shadow-lg">⭐</span>
        <div>
          <h3 className="font-extrabold text-[#3B0764] text-xl">Job Boost</h3>
          <p className="text-sm text-gray-500">
            Give one job higher visibility and priority placement. Pick a job from your{' '}
            <Link to="/employer/jobs" className="text-[#9333EA] font-semibold hover:underline">Jobs page</Link>{' '}
            to boost it.
          </p>
        </div>
      </div>

      {jobBoosts.length === 0 ? (
        <EmptyState text="No boost tiers available right now." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobBoosts.map((tier) => {
            const isBest = tier.id === bestBoostId;
            const perDay = Number(tier.price) / Number(tier.duration_days || 1);
            return (
              <div
                key={tier.id}
                className={`relative rounded-2xl overflow-hidden border-2 bg-white transition duration-200 hover:-translate-y-1 hover:shadow-2xl ${
                  isBest ? 'border-[#EC4899] shadow-xl' : 'border-[#F3E8FF] shadow-md'
                }`}
              >
                {isBest && (
                  <span className="absolute top-3 right-3 z-10 text-[10px] font-bold bg-white text-[#EC4899] px-3 py-1 rounded-full shadow">
                    Best Value
                  </span>
                )}

                <div className="relative bg-gradient-to-br from-[#6D28D9] via-[#9333EA] to-[#EC4899] text-white px-6 pt-6 pb-8">
                  <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10"></div>
                  <div className="absolute -left-8 -bottom-10 w-28 h-28 rounded-full bg-white/10"></div>
                  <p className="relative text-xs font-bold uppercase tracking-widest text-white/80">Boost pack</p>
                  <p className="relative text-5xl font-extrabold leading-none mt-2">{tier.duration_days}</p>
                  <p className="relative text-sm text-white/90 mt-1">Days</p>
                </div>

                <div className="p-6">
                  <div className="flex items-end justify-between mb-4">
                    <p className="text-3xl font-extrabold text-[#3B0764]">₹{Number(tier.price).toLocaleString('en-IN')}</p>
                    <p className="text-xs font-semibold text-[#9333EA] bg-[#FDF4FF] px-2.5 py-1 rounded-full">
                      ₹{perDay.toFixed(1)} / day
                    </p>
                  </div>

                  <ul className="space-y-2 mb-6 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>
                      Higher search visibility
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>
                      Boost badge on your job
                    </li>
                  </ul>

                  <button
                    type="button"
                    onClick={() => setGrantConfirm({ type: 'job_boost', name: String(tier.id), item: `${tier.duration_days}-day Job Boost`, price: tier.price, validity: `${tier.duration_days} days once applied to a job` })}
                    disabled={buyingGrant === `job_boost:${tier.id}`}
                    className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#9333EA] to-[#EC4899] hover:shadow-lg disabled:opacity-50 transition"
                  >
                    {buyingGrant === `job_boost:${tier.id}` ? 'Opening…' : 'Buy Boost'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
})()}

                {activeTab === 'boost' && (
                  <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 border border-[#F3E8FF]">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#9333EA] to-[#EC4899] text-white text-2xl flex items-center justify-center shadow-lg">🌟</span>
                      <div>
                        <h3 className="font-extrabold text-[#3B0764] text-xl">Featured Job</h3>
                        <p className="text-sm text-gray-500">Featured badge, highlighted listing and more visibility. Pay, then choose the job.</p>
                      </div>
                    </div>

                    {featuredOptions.length === 0 ? (
                      <EmptyState text="Featured Job is not available right now." />
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featuredOptions.map((opt) => (
                          <div key={opt.id} className="rounded-2xl border-2 border-[#F3E8FF] shadow-md p-6 bg-white">
                            <p className="text-xs font-bold uppercase tracking-widest text-[#A855F7]">{opt.name}</p>
                            <p className="text-3xl font-extrabold text-[#3B0764] mt-2">₹{Number(opt.price).toLocaleString('en-IN')}</p>
                            <p className="text-xs text-gray-400 mb-4">per placement · {opt.duration_days} days</p>
                            <ul className="space-y-2 mb-6 text-sm text-gray-600">
                              <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>Featured badge</li>
                              <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>Priority / highlighted listing</li>
                              <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#DB2777] shrink-0"></span>Increased visibility</li>
                            </ul>
                            <button
                              type="button"
                              onClick={() => setGrantConfirm({ type: 'featured_job', name: String(opt.id), item: `Featured Job (${opt.duration_days} days)`, price: opt.price, validity: `${opt.duration_days} days once applied to a job` })}
                              disabled={buyingGrant === `featured_job:${opt.id}`}
                              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#9333EA] to-[#EC4899] hover:shadow-lg disabled:opacity-50 transition"
                            >
                              {buyingGrant === `featured_job:${opt.id}` ? 'Opening…' : 'Buy Featured Job'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {(unused.featured_job > 0 || unused.job_boost > 0) && (
                      <div className="mt-6 bg-[#FDF4FF] border border-[#F3E8FF] rounded-xl p-4">
                        <p className="text-sm font-bold text-[#3B0764] mb-3">
                          You have {unused.featured_job > 0 ? `${unused.featured_job} Featured Job` : ''}
                          {unused.featured_job > 0 && unused.job_boost > 0 ? ' and ' : ''}
                          {unused.job_boost > 0 ? `${unused.job_boost} Job Boost` : ''} ready to use. Choose a job:
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <select
                            value={applyJobId}
                            onChange={(e) => setApplyJobId(e.target.value)}
                            className="flex-1 min-w-[200px] border border-[#E9D5FF] rounded-lg px-3 py-2 text-sm bg-white"
                          >
                            <option value="">Select an active job</option>
                            {myJobs.map((j) => (
                              <option key={j.id} value={j.id}>{j.title}</option>
                            ))}
                          </select>
                          {unused.featured_job > 0 && (
                            <button type="button" disabled={applying} onClick={() => handleApplyCredit('featured_job')}
                              className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-[#9333EA] to-[#EC4899] disabled:opacity-50">
                              Feature this job
                            </button>
                          )}
                          {unused.job_boost > 0 && (
                            <button type="button" disabled={applying} onClick={() => handleApplyCredit('job_boost')}
                              className="px-4 py-2 rounded-lg text-sm font-bold border-2 border-[#9333EA] text-[#9333EA] disabled:opacity-50">
                              Boost this job
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                         <BillingHistory records={records} />
                         <PricingFaq />
                         </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployerBilling;
